/**
 * Ticket 137.7 — typed API client for the Connect Framework admin surface
 * (backend: ticket 137.6, `internal/connectadmin`).
 *
 * Deliberately connector-agnostic: every type here is shaped by a
 * connector's `authKind` + `configSchema`, never by a specific connector
 * name. No Bitrix (or any other connector) special-casing belongs here.
 */

import { apiClient } from '@/lib/apiClient';

// ------------------------------------------------------------------ //
// Catalogue (GET /admin/connectors)
// ------------------------------------------------------------------ //

export type AuthKind = 'OAuth2AuthCode' | 'APIKey' | 'HMACBearer';

export interface ConnectorConfigField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  description: string;
}

export interface ConnectorCatalogueEntry {
  type: string;
  displayName: string;
  authKind: AuthKind;
  configSchema: ConnectorConfigField[];
  capabilities: string[];
}

export async function getConnectorCatalogue(): Promise<ConnectorCatalogueEntry[]> {
  const { data } = await apiClient.get<{ connectors: ConnectorCatalogueEntry[] }>(
    '/admin/connectors',
  );
  return data.connectors ?? [];
}

// ------------------------------------------------------------------ //
// Connections (POST/GET /admin/tenants/{tenantId}/connections, test, disconnect)
// ------------------------------------------------------------------ //

export type ConnectionStatus = 'CONNECTED' | 'DEGRADED' | 'DISCONNECTED';

export interface Connection {
  connectorType: string;
  instanceLabel: string;
  status: ConnectionStatus;
  statusReason?: string;
  connectedAt: string;
  config?: Record<string, unknown>;
}

export interface StartConnectInput {
  connectorType: string;
  instanceLabel?: string;
  config?: Record<string, string>;
  secrets?: Record<string, string>;
  redirectUri?: string;
}

/**
 * The backend's POST /connections has two distinct success shapes (see
 * connectadmin.startConnect): an OAuth2AuthCode connector 200s with
 * `{authorizeUrl}` and persists nothing; an APIKey/HMACBearer connector
 * validates via HealthCheck and 201s with the established Connection.
 */
export type StartConnectResult = { authorizeUrl: string } | Connection;

export function isAuthorizeUrlResult(
  result: StartConnectResult,
): result is { authorizeUrl: string } {
  return 'authorizeUrl' in result;
}

export async function startConnect(
  tenantId: string,
  input: StartConnectInput,
): Promise<StartConnectResult> {
  const { data } = await apiClient.post<StartConnectResult>(
    `/admin/tenants/${tenantId}/connections`,
    input,
  );
  return data;
}

export async function listConnections(tenantId: string): Promise<Connection[]> {
  const { data } = await apiClient.get<{ connections: Connection[] }>(
    `/admin/tenants/${tenantId}/connections`,
  );
  return data.connections ?? [];
}

export interface TestConnectionResult {
  status: ConnectionStatus;
  reason?: string;
}

export async function testConnection(
  tenantId: string,
  connectorType: string,
): Promise<TestConnectionResult> {
  const { data } = await apiClient.post<TestConnectionResult>(
    `/admin/tenants/${tenantId}/connections/${connectorType}/test`,
  );
  return data;
}

export interface DisconnectResult {
  tenantId: string;
  connectorType: string;
  status: ConnectionStatus;
}

export async function disconnect(
  tenantId: string,
  connectorType: string,
): Promise<DisconnectResult> {
  const { data } = await apiClient.delete<DisconnectResult>(
    `/admin/tenants/${tenantId}/connections/${connectorType}`,
  );
  return data;
}

// ------------------------------------------------------------------ //
// Status (GET /admin/tenants/{tenantId}/connections/{type}/status) — 137.8
// ------------------------------------------------------------------ //

export interface BacklogStatus {
  pending: number;
}

export interface SyncStateEntry {
  syncKey: string;
  highWater: string;
}

export interface CooldownStatus {
  active: boolean;
  until?: string;
  note: string;
}

export interface ConnectionStatusDetail {
  connectorType: string;
  instanceLabel: string;
  status: ConnectionStatus;
  statusReason?: string;
  connectedAt: string;
  backlog: BacklogStatus;
  lastSync: SyncStateEntry[];
  cooldown: CooldownStatus;
  fallbackServing: boolean;
  delegatedCapabilities?: string[];
}

export async function getConnectionStatus(
  tenantId: string,
  connectorType: string,
): Promise<ConnectionStatusDetail> {
  const { data } = await apiClient.get<ConnectionStatusDetail>(
    `/admin/tenants/${tenantId}/connections/${connectorType}/status`,
  );
  return data;
}

// ------------------------------------------------------------------ //
// Capability delegation (PUT/DELETE .../capabilities/{capability}/provider)
// ------------------------------------------------------------------ //

export const NATIVE_PROVIDER_NAME = 'native';

export interface CapabilityBindingResult {
  tenantId: string;
  capability: string;
  provider: string;
  enabled: boolean;
}

export async function setCapabilityProvider(
  tenantId: string,
  capability: string,
  providerName: string,
): Promise<CapabilityBindingResult> {
  const { data } = await apiClient.put<CapabilityBindingResult>(
    `/admin/tenants/${tenantId}/capabilities/${capability}/provider`,
    { provider: providerName },
  );
  return data;
}

export async function clearCapabilityProvider(
  tenantId: string,
  capability: string,
): Promise<CapabilityBindingResult> {
  const { data } = await apiClient.delete<CapabilityBindingResult>(
    `/admin/tenants/${tenantId}/capabilities/${capability}/provider`,
  );
  return data;
}
